import { useQuery } from '@tanstack/react-query';
import { usePathname, useSearchParams } from 'next/navigation';
import { lazy } from 'react';
import { ReactNode, Suspense, useCallback, useEffect, useState } from 'react';

import {
	getFooter,
	getMainNav,
	getPartnerLogos,
} from 'services/mancity-template';

import { useBrowsing } from 'hooks/use-browsing';

import {
	PUBLIC_FF_HEADER_BASKET_ICON,
	PUBLIC_GTM_ID_ENV,
} from 'tools/environments';

import styles from './layout.module.scss';

type LayoutProps = {
	children: ReactNode;
};

const ManCityTemplate = lazy(() => import('ui/mancity/template'));
const SearchHeader = lazy(
	() => import('core/mancity/components/headers/search-header')
);

const analyticsConfig = {
	gtmId: PUBLIC_GTM_ID_ENV,
};

export const Layout = ({ children }: LayoutProps) => {
	const { query } = useBrowsing();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const [isH1Title, setIsH1Title] = useState(false);

	const isFromApp = query.fromapp !== undefined ? true : false;

	const footerQuery = useQuery({
		queryKey: ['footer'],
		queryFn: () => getFooter().then((response) => response.data),
		enabled: !isFromApp,
	});

	const mainNavQuery = useQuery({
		queryKey: ['main-nav'],
		queryFn: () => getMainNav().then((response) => response.data),
		enabled: !isFromApp,
	});

	const partnerLogosQuery = useQuery({
		queryKey: ['partners'],
		queryFn: () => getPartnerLogos().then((response) => response.data),
		enabled: !isFromApp,
	});

	const navItems =
		mainNavQuery.data?.find(({ navgroup }) => navgroup === 'primary')
			?.items || [];

	const searchNavigationHandle = useCallback((value: string) => {
		if (value) {
			window.location.href = `/help/search-results?term=${encodeURIComponent(
				value
			)}`;
		}
	}, []);

	const triggerQualtricsSurvey = () => {
		if (window.QSI && window.QSI.API) {
			window.QSI.API.unload();
			window.QSI.API.load();
			window.QSI.API.run();
			console.log('Qualtrics script ran successfully.');
		}
	};

	useEffect(() => {
		if (pathname.includes('/article/')) {
			triggerQualtricsSurvey();
		}
		setIsH1Title(
			!(pathname.includes('/sections') || pathname.includes('/article'))
		);
	}, [pathname, searchParams]);

	if (footerQuery.isFetched) {
		return (
			<Suspense>
				<ManCityTemplate
					headerNavigationData={navItems}
					partnersData={partnerLogosQuery.data}
					footerData={footerQuery.data}
					hideTemplateRoutes={['/preferences/partial']}
					config={analyticsConfig}
					showBasketIcon={PUBLIC_FF_HEADER_BASKET_ICON}
				>
					<div className={styles.container}>
						<SearchHeader
							title="How can we help you?"
							isH1Title={isH1Title}
							callback={searchNavigationHandle}
						/>
						<div className={styles.main}>
							<div className={`${styles.grid} container`}>
								<div className="row">
									<div className="col-12">{children}</div>
								</div>
							</div>
						</div>
					</div>
				</ManCityTemplate>
			</Suspense>
		);
	}
};
